import { CollaboratorUserContextProps } from "contexts-types";
import { CollaboratorUserController } from "controllers/CollaboratorUserController";
import { Columns, FilterProps, Props } from "props";
import React, { createContext, useContext, useEffect, useState } from "react";
import { useInfiniteQuery } from "react-query";
import {
  ICollaboratorUserInfosViewModel,
  ICollaboratorUserViewModel,
} from "viewModels";
import { useAuth } from "./auth";
import { IOrderFieldInputModel } from "inputModels";
import useWebSocket from "hooks/useWebSocket";

const PAGE_SIZE = 15;
const CollaboratorUserContext = createContext<CollaboratorUserContextProps>(
  {} as CollaboratorUserContextProps
);

const CollaboratorUser: React.FC<Props> = ({ children }) => {
  const { user, setUser, logged } = useAuth();
  const [orderBy, setOrder] = useState<IOrderFieldInputModel<Columns>[]>([]);
  const [collaboratorUsers, setCollaboratorUsers] = useState<
    ICollaboratorUserViewModel[]
  >([]);

  const [filter, setFilter] = useState<FilterProps>();

  const {
    data: results,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    refetch,
    isLoading,
  } = useInfiniteQuery(
    ["collaboratorUsers", orderBy, filter],
    ({ pageParam = 1 }) =>
      CollaboratorUserController.getCollaboratorByUserId(user.id, {
        orderBy,
        page: pageParam,
        take: 15,
        term: filter?.term,
        filter: filter?.term ? filter?.filter : undefined,
      }).then((res) => res.data),
    {
      enabled: !!user?.id && logged,
      refetchOnWindowFocus: false,
      getNextPageParam: (lastPage, allPages) => {
        const nextPage = allPages.length + 1;
        return lastPage.length < PAGE_SIZE ? undefined : nextPage;
      },
    }
  );

  const handleFetch = () => {
    refetch();
  };

  const handleFetchNextPage = () => {
    fetchNextPage();
  };

  // Atualiza os status de gerencia em tempo real
  useWebSocket<ICollaboratorUserInfosViewModel>({
    url: `update-collaborator/${user?.id}`,
    onMessage: (data) => {
      setUser((prev) => ({
        ...prev,
        managementStatus: data.managementStatus,
      }));
    },
  });

  useEffect(() => {
    setCollaboratorUsers(results?.pages?.flat() ?? []);
  }, [results, setCollaboratorUsers]);

  return (
    <CollaboratorUserContext.Provider
      value={{
        collaboratorUsers,
        setCollaboratorUsers,
        orderBy,
        setOrder,
        filter,
        setFilter,
        handleFetch,
        handleFetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        isLoading,
      }}
    >
      {children}
    </CollaboratorUserContext.Provider>
  );
};

export const useCollaboratorUser: () => CollaboratorUserContextProps = () =>
  useContext(CollaboratorUserContext);

export default CollaboratorUser;
